import { FormValidate } from '@/components';
import { BodyForm } from '@/components/formValidate';
import { minLength } from '@/components/formValidate/validates/minLength';
import ShowHide from '@/components/showHide';
import { Tabs } from '@/components/tabs/tabs';
import { useAssetImageValidation } from '@/modules/assetImage/hooks/useAssetImageValidation';
import { customizationsTabs } from '@/modules/customization/entities/constants/customization.constants';
import { CUSTOMIZATION_TAB_ID } from '@/modules/customization/entities/enums/customizations.enums';
import { FormAppCustomizableItems } from '@/modules/customization/entities/interfaces/customizations.interfaces';
import { useCRUDAppCustomization } from '@/modules/customization/hooks/useCRUDAppCustomization';
import { BannerCustomization } from '@/modules/customization/views/bannerCustomization';
import { LogoCustomization } from '@/modules/customization/views/logoCustomization';
import { useLoadImage } from '@/shared/hooks/useLoadImage/useLoadImage';
import removeEmptyParams from '@/utils/removeEmptyParams';
import { replaceEmptyFieldsFromObject } from '@/utils/replaceEmptiesFromObject/replaceEmptiesFromObject';
import { get, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from 'vkit/lib/context';

const Home: React.FC = () => {
  const formAppCustomizationDefault: Partial<FormAppCustomizableItems> = {
    bannerDescription: 'Descubra as novidades que preparamos para você.',
    buttonLabel: 'Saiba mais',
  };

  const [shouldHasLink, setShouldHasLink] = useState(true);
  const [currentTab, setCurrentTab] = useState(CUSTOMIZATION_TAB_ID.LOGO);

  const [logoFileUrl, setLogoFileUrl] = useState<string | null>();
  const [bannerFileUrl, setBannerFileUrl] = useState<string | null>();

  const [formAppCustomization, setFormAppCustomization] = useState<FormAppCustomizableItems>({
    logoFile: null,

    bannerFile: null,
    bannerDescription: '',
    bannerInitialDate: '',
    bannerEndDate: '',

    buttonLabel: '',
    buttonLink: '',
  });

  const initialState = useRef({
    hasBannerFile: false,
    hasLogoFile: false,

    bannerDescription: '',
    bannerInitialDate: '',
    bannerEndDate: '',

    buttonLabel: '',
    buttonLink: '',
  });

  const { isBeforeEndDate, isAfterInitialDate } = useAssetImageValidation({
    assetImage: {
      initialDate: formAppCustomization.bannerInitialDate,
      endDate: formAppCustomization.bannerEndDate,
    },
  });

  const {
    appCustomization,
    loadingAppCustomization,
    createAppCustomization,
    creatingAppCustomization,
    updateAppCustomization,
    updatingAppCustomization,
    deleteLogo,
    deletingLogo,
    deleteBanner,
    deletingBanner,
  } = useCRUDAppCustomization();

  const hasBannerFile = useMemo(
    () => Boolean(formAppCustomization.bannerFile || bannerFileUrl),
    [bannerFileUrl, formAppCustomization.bannerFile],
  );

  const hasLogoFile = useMemo(
    () => Boolean(formAppCustomization.logoFile || logoFileUrl),
    [formAppCustomization.logoFile, logoFileUrl],
  );

  const hasBannerText = useMemo(() => {
    return !isEmpty(
      removeEmptyParams({
        bannerDescription: formAppCustomization.bannerDescription,
        bannerInitialDate: formAppCustomization.bannerInitialDate,
        bannerEndDate: formAppCustomization.bannerEndDate,

        buttonLabel: formAppCustomization.buttonLabel,
        buttonLink: formAppCustomization.buttonLink,
      }),
    );
  }, [
    formAppCustomization.bannerDescription,
    formAppCustomization.bannerEndDate,
    formAppCustomization.bannerInitialDate,
    formAppCustomization.buttonLabel,
    formAppCustomization.buttonLink,
  ]);

  useEffect(() => {
    if (!appCustomization || loadingAppCustomization) {
      return;
    }

    const buttonLabel = get(appCustomization, 'bannerButton.description');
    const buttonLink = get(appCustomization, 'bannerButton.link');

    setBannerFileUrl(appCustomization.banner?.url);
    setLogoFileUrl(appCustomization.logo?.url);
    setShouldHasLink(appCustomization.banner?.url ? Boolean(buttonLabel || buttonLink) : true);

    setFormAppCustomization({
      logoFile: null,

      bannerFile: null,
      bannerDescription: get(appCustomization, 'banner.description'),
      bannerInitialDate: get(appCustomization, 'banner.initialDate'),
      bannerEndDate: get(appCustomization, 'banner.endDate'),

      buttonLabel,
      buttonLink,
    });

    initialState.current = {
      hasBannerFile: Boolean(appCustomization.banner?.url),
      hasLogoFile: Boolean(appCustomization.logo?.url),

      bannerDescription: get(appCustomization, 'banner.description', ''),
      bannerInitialDate: get(appCustomization, 'banner.initialDate', ''),
      bannerEndDate: get(appCustomization, 'banner.endDate', ''),

      buttonLabel: get(appCustomization, 'bannerButton.description', ''),
      buttonLink: get(appCustomization, 'bannerButton.link', ''),
    };
  }, [appCustomization, loadingAppCustomization]);

  const { loading: loadingLogoFile } = useLoadImage(logoFileUrl);
  const { loading: loadingBannerFile } = useLoadImage(bannerFileUrl);

  const formBody = ({ onBlur, onChange, useErrors, useValues }: BodyForm) => (
    <>
      <ShowHide visible={currentTab === CUSTOMIZATION_TAB_ID.LOGO} transition='slideToDown'>
        <LogoCustomization
          file={formAppCustomization.logoFile}
          setFile={(file: File | null) =>
            setFormAppCustomization((current) => ({
              ...current,
              logoFile: file,
            }))
          }
          loading={loadingAppCustomization || deletingLogo || loadingLogoFile}
          savedFileUrl={logoFileUrl}
          onRemoveFile={async () => {
            setLogoFileUrl(null);
          }}
        />
      </ShowHide>
      <ShowHide visible={currentTab === CUSTOMIZATION_TAB_ID.BANNER} transition='slideToDown'>
        <BannerCustomization
          formAppCustomizationDefault={formAppCustomizationDefault}
          formAppCustomization={useValues as FormAppCustomizableItems}
          onChange={onChange}
          onBlur={onBlur}
          errors={useErrors}
          shouldHasLink={shouldHasLink}
          setShouldHasLink={(value) => {
            setShouldHasLink(value);
            setFormAppCustomization((current) => ({
              ...current,
              buttonLabel: '',
              buttonLink: '',
            }));
          }}
          loading={loadingAppCustomization || deletingLogo || loadingBannerFile}
          savedFileUrl={bannerFileUrl}
          onRemoveFile={async () => {
            setBannerFileUrl(null);
          }}
          onCleanFields={() => {
            setFormAppCustomization((current) => ({
              ...current,
              bannerDescription: '',
              bannerInitialDate: '',
              bannerEndDate: '',
              buttonLabel: '',
              buttonLink: '',
            }));
          }}
        />
      </ShowHide>
    </>
  );

  return (
    <Grid column stretch gap={16}>
      <Tabs
        items={customizationsTabs}
        initialActiveTab={CUSTOMIZATION_TAB_ID.LOGO}
        setTabKey={setCurrentTab}
      />
      <FormValidate
        onChangeForm={setFormAppCustomization}
        onSubmit={async () => {
          if (!hasLogoFile && initialState.current.hasLogoFile && appCustomization) {
            await deleteLogo(appCustomization);
          }

          if (!hasBannerFile && initialState.current.hasBannerFile && appCustomization) {
            await deleteBanner(appCustomization);
          }

          let dataToSave: Partial<FormAppCustomizableItems> = {};
          if (hasBannerFile) {
            dataToSave = replaceEmptyFieldsFromObject(formAppCustomization, {
              bannerDescription: formAppCustomizationDefault.bannerDescription,
              buttonLabel: shouldHasLink ? formAppCustomizationDefault.buttonLabel : undefined,
            });
          }

          if (hasLogoFile) {
            dataToSave.logoFile = formAppCustomization.logoFile;
          }

          appCustomization?.customizationId
            ? await updateAppCustomization(appCustomization?.customizationId, dataToSave)
            : await createAppCustomization(dataToSave as FormAppCustomizableItems);
        }}
        fields={{
          logoFile: [],

          bannerFile: hasBannerText && !hasBannerFile ? ['required'] : [],
          bannerDescription: [minLength(3)],
          bannerInitialDate: hasBannerFile ? ['required', 'date', isBeforeEndDate] : [],
          bannerEndDate: ['date', isAfterInitialDate],

          buttonLabel: [minLength(3)],
          buttonLink: hasBannerFile && shouldHasLink ? ['required', minLength(3)] : [],
        }}
        body={formBody}
        resource={formAppCustomization}
        buttons={{
          submit: {
            text: 'Salvar',
            loading:
              loadingAppCustomization ||
              creatingAppCustomization ||
              updatingAppCustomization ||
              deletingBanner ||
              deletingLogo,
          },
        }}
      />
    </Grid>
  );
};

export default Home;
